import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { COLLAPSED_KEY, MOBILE_MAX } from "./sidebarRules";

/** Fired on window by the top bar's menu button. */
export const SIDEBAR_TOGGLE_EVENT = "sidebar-toggle";

const isMobileWidth = () => window.innerWidth <= MOBILE_MAX;

/**
 * Sidebar size, kept on <body> with the layout's classes (the page and top bar already follow them):
 * - wide screens: full or collapsed to icons (body.sidebar-mini), remembered in localStorage;
 * - up to 1024px: hidden (body.sidebar-gone) or slid in over the page (body.sidebar-show), closed on navigation.
 */
export default function useSidebarLayout() {
  const { pathname } = useLocation();
  const [mobile, setMobile] = useState(isMobileWidth);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setMobile(isMobileWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const toggle = () => {
      if (isMobileWidth()) setMobileOpen((o) => !o);
      else
        setCollapsed((c) => {
          localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
          return !c;
        });
    };
    window.addEventListener(SIDEBAR_TOGGLE_EVENT, toggle);
    return () => window.removeEventListener(SIDEBAR_TOGGLE_EVENT, toggle);
  }, []);

  useEffect(() => setMobileOpen(false), [pathname, mobile]);

  useEffect(() => {
    const body = document.body.classList;
    body.toggle("sidebar-mini", !mobile && collapsed);
    body.toggle("sidebar-gone", mobile && !mobileOpen);
    body.toggle("sidebar-show", mobile && mobileOpen);
  }, [mobile, collapsed, mobileOpen]);

  // Esc closes the slide-in menu.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  return useMemo(() => ({ mobile, collapsed: !mobile && collapsed, mobileOpen, closeMobile }), [mobile, collapsed, mobileOpen, closeMobile]);
}

/** Opens / closes the sidebar (top bar menu button). */
export const toggleSidebar = () => window.dispatchEvent(new Event(SIDEBAR_TOGGLE_EVENT));
